import React from "react";
import { Btn, Card, Tag } from "@app/shared/src/components_v2";

/**
 * A device settings section that either follows the fleet default or has been overridden.
 *
 * Inheritance is derived, not flagged: a unit follows the default while its values still match the
 * organization's stored defaults, and the moment someone changes one it has diverged. That is more
 * honest than a stored "overridden" bit, which can disagree with the values it claims to describe —
 * and it makes "put this truck back on the fleet default" a real, checkable action.
 *
 * While inherited, the controls are dimmed and inert behind a veil rather than hidden: a director
 * needs to read what the unit is running before deciding to break it away from the fleet.
 */
export const InheritedSection: React.FC<{
    /** Section name as it reads in a sentence — "Alerts & Relays". */
    title: string;
    inherited: boolean;
    onOverride: () => void;
    onResetToDefault?: () => void;
    /**
     * Which settings differ from the fleet default, in words a director would use.
     *
     * "Overridden" on its own invites the question it should be answering. Naming the fields is
     * also the only way to tell a deliberate change apart from drift someone else introduced.
     */
    differences?: string[];
    /**
     * The unit is missing pieces it is supposed to have — a trigger, a zone, an endpoint — and
     * this sentence says which, and that resetting to the fleet default creates them.
     *
     * A separate state from "overridden" because it needs a different tone: a truck someone tuned
     * deliberately is a choice, a truck that was never set up is a fault. Callers that pass this
     * must also fold it into `inherited`, or the veil would read "follows the fleet default" over
     * a unit that cannot do what the default asks.
     */
    needsSetup?: string;
    children: React.ReactNode;
}> = ({ title, inherited, onOverride, onResetToDefault, differences = [], needsSetup, children }) => (
    <div className="relative">
        {/*
          * Spaces its own children: a section is a stack of cards, and every caller was otherwise
          * left to remember a gap wrapper. The ones that forgot rendered cards flush against each
          * other while their neighbours breathed.
          */}
        <div
            className={`flex flex-col gap-4 ${inherited ? "pointer-events-none select-none opacity-40 saturate-[.55]" : ""}`}
        >
            {children}
        </div>

        {inherited ? (
            <div className="absolute inset-0 z-[3] flex items-start justify-center px-5 pt-[74px]">
                <Card className="flex max-w-[520px] flex-col items-center gap-3.5 px-6 py-5 text-center shadow-[0_24px_60px_rgba(0,0,0,.45)]">
                    <p className="max-w-[46ch] text-[13.5px] leading-relaxed text-[var(--muted)]">
                        This unit follows the <b className="font-semibold text-[var(--text)]">fleet default</b> for {title}.
                        Overriding stops it tracking changes you make in Settings.
                    </p>
                    <Btn onClick={onOverride}>Override defaults</Btn>
                </Card>
            </div>
        ) : needsSetup ? (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 px-1">
                <Tag tone="crit">Not set up</Tag>
                <span className="text-[12.5px] text-[var(--muted)]">{needsSetup}</span>
                {onResetToDefault && (
                    <button
                        type="button"
                        onClick={onResetToDefault}
                        className="ml-auto whitespace-nowrap text-[12.5px] font-semibold text-[var(--accent)]"
                    >
                        Reset to fleet default
                    </button>
                )}
            </div>
        ) : (
            onResetToDefault && (
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 px-1">
                    <Tag tone="warn">Overridden</Tag>
                    <span className="text-[12.5px] text-[var(--muted)]">
                        {differences.length > 0 ? (
                            <>
                                This unit differs from the fleet default on{" "}
                                <b className="font-semibold text-[var(--text)]">{differences.join(", ")}</b>.
                            </>
                        ) : (
                            <>This unit no longer tracks the fleet default for {title}.</>
                        )}
                    </span>
                    <button
                        type="button"
                        onClick={onResetToDefault}
                        className="ml-auto whitespace-nowrap text-[12.5px] font-semibold text-[var(--accent)]"
                    >
                        Reset to fleet default
                    </button>
                </div>
            )
        )}
    </div>
);
